/**
 * sceneKeys — canonical Phaser scene-key constants for the entry chain.
 *
 * Pulled into its own Phaser-free module so the keys can be referenced by
 * unit tests (which never instantiate a scene) and by `scene.start(...)`
 * callers without an import cycle through the Scene classes themselves.
 *
 * The chain (per build-plan-v2 §1 + GDD §14.3):
 *   Boot → Preloader → Title → CharacterCreation? → Overworld
 *                                         (fallback ↘)
 */

export const BOOT_SCENE_KEY = 'BootScene' as const;
export const PRELOADER_SCENE_KEY = 'PreloaderScene' as const;
export const TITLE_SCENE_KEY = 'TitleScene' as const;
export const CHARACTER_CREATION_SCENE_KEY = 'CharacterCreationScene' as const;
export const OVERWORLD_SCENE_KEY = 'OverworldScene' as const;
export const PAINTED_WORLD_COORDINATOR_SCENE_KEY = 'PaintedWorldCoordinator' as const;
export const UI_SCENE_KEY = 'UIScene' as const;
export const SLEEP_OVERLAY_SCENE_KEY = 'SleepOverlayScene' as const;
export const THRESHOLD_SCENE_KEY = 'ThresholdScene' as const;
export const JOURNAL_SCENE_KEY = 'JournalScene' as const;
export const RELIQUARY_SCENE_KEY = 'ReliquaryScene' as const;
export const WORLD_GALLERY_SCENE_KEY = 'WorldGalleryScene' as const;
export const FAINT_OVERLAY_SCENE_KEY = 'FaintOverlayScene' as const;
export const AMBIENT_OVERLAY_SCENE_KEY = 'AmbientOverlayScene' as const;
export const SCENE_SELECTOR_SCENE_KEY = 'SceneSelectorScene' as const;
export const BUILD_TOOLS_SCENE_KEY = 'BuildToolsScene' as const;
export const EDITOR_SCENE_KEY = 'EditorScene' as const;
/** SMA-42 Phase 2 — controller scene for the painted-world overworld. */
export const PAINTED_OVERWORLD_SHELL_SCENE_KEY = 'PaintedOverworldShell' as const;

/**
 * Ordered list of the entry-chain scene keys. Index 0 is the auto-start
 * scene per Phaser's GameConfig contract. UIScene is launched in parallel
 * by the world scene and is appended last so Phaser does not auto-start it.
 */
export const ENTRY_SCENE_KEYS = [
  BOOT_SCENE_KEY,
  PRELOADER_SCENE_KEY,
  TITLE_SCENE_KEY,
  CHARACTER_CREATION_SCENE_KEY,
  PAINTED_WORLD_COORDINATOR_SCENE_KEY,
  UI_SCENE_KEY,
] as const;

/**
 * Registry keys written by BootScene before any other scene runs. Tests
 * assert this set to catch accidental drift when new globals are added.
 */
export const BOOT_REGISTRY_KEYS = [
  'app:version',
  'app:theme',
  'app:bootedAtMs',
  'app:audioMuted',
] as const;

export type BootRegistryKey = (typeof BOOT_REGISTRY_KEYS)[number];

/**
 * Texture key for the magenta-checker placeholder used when an art asset
 * fails to load. Generated by BootScene so every later scene can depend on
 * its existence.
 */
export const MISSING_TEXTURE_KEY = '__MISSING';
